import * as React from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"

import { cn } from "@/utils/utils"
import LureoButton from "@/common/LureoButton"
import { BLOCKING, DIALOG_TITLE, DIALOG_WIDTHS, SCRIM, type DialogWidth } from "@/components/ui/recipes"

// A question that blocks until answered (delete this? leave without saving?): the dialog's card and parts, no close
// button, its actions the answers. Escape and a click on the scrim answer as Cancel does, as every dialog closes both
// ways; one that must not close yet ignores the close in its onOpenChange, which both go through.

// Closes the dialog from its scrim, the way Escape does
const DismissContext = React.createContext<() => void>(() => undefined)

function AlertDialog({ open: openProp, defaultOpen = false, onOpenChange, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  const [openState, setOpenState] = React.useState(defaultOpen)
  const open = openProp ?? openState
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setOpenState(next)
    onOpenChange?.(next)
  }
  return (
    <DismissContext.Provider value={() => setOpen(false)}>
      <AlertDialogPrimitive.Root open={open} onOpenChange={setOpen} {...props} />
    </DismissContext.Provider>
  )
}

const AlertDialogTrigger = AlertDialogPrimitive.Trigger

const AlertDialogPortal = AlertDialogPrimitive.Portal

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      SCRIM,
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:ease-lureo-easing-enter data-[state=closed]:ease-lureo-easing-exit data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
))
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> & { width?: DialogWidth; container?: HTMLElement | null }
>(({ className, width = "md", container, ...props }, ref) => {
  const dismiss = React.useContext(DismissContext)
  return (
  <AlertDialogPortal container={container}>
    <AlertDialogOverlay onClick={dismiss} />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-lureo-layer-overlay flex max-h-[90vh] w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-lureo-radius-dialog font-lureo-font-ui duration-lureo-duration-moderate data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:ease-lureo-easing-enter data-[state=closed]:ease-lureo-easing-exit data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-3 data-[state=closed]:slide-out-to-bottom-3",
        BLOCKING,
        DIALOG_WIDTHS[width],
        className
      )}
      {...props}
    />
  </AlertDialogPortal>
  )
})
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="dialog-header"
    className={cn(
      "flex min-h-14 shrink-0 flex-col justify-center gap-2 border-b-lureo-border-width border-lureo-line px-6 py-3 text-left [&:has(+[data-slot=dialog-footer])]:min-h-0 [&:has(+[data-slot=dialog-footer])]:border-b-0 [&:has(+[data-slot=dialog-footer])]:pb-0 [&:has(+[data-slot=dialog-footer])]:pt-6",
      className
    )}
    {...props}
  />
)
AlertDialogHeader.displayName = "AlertDialogHeader"

const AlertDialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6 py-5 text-lureo-14 text-lureo-ink leading-5",
      className
    )}
    {...props}
  />
)
AlertDialogBody.displayName = "AlertDialogBody"

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="dialog-footer"
    className={cn(
      // Straight after the header (a dialog with no body) it has no line, and the dialog's padding below it
      "flex shrink-0 flex-col-reverse gap-2 border-t-lureo-border-width border-lureo-line px-6 py-3 sm:flex-row sm:items-center sm:justify-end [[data-slot=dialog-header]+&]:border-t-0 [[data-slot=dialog-header]+&]:pb-6 [[data-slot=dialog-header]+&]:pt-4",
      className
    )}
    {...props}
  />
)
AlertDialogFooter.displayName = "AlertDialogFooter"

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn(DIALOG_TITLE, className)}
    {...props}
  />
))
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn("text-lureo-14 text-lureo-ink-subtle leading-5", className)}
    {...props}
  />
))
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action asChild>
    <LureoButton
      ref={ref}
      variant="secondary"
      suppressTooltip
      className={cn("h-9 px-4 rounded-lureo-radius-button-pill", className)}
      {...props}
    />
  </AlertDialogPrimitive.Action>
))
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel asChild>
    <LureoButton
      ref={ref}
      variant="outline-2"
      suppressTooltip
      className={cn("h-9 px-5 rounded-lureo-radius-button-pill", className)}
      {...props}
    />
  </AlertDialogPrimitive.Cancel>
))
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}
